<script setup lang="ts">
import { type PropType } from 'vue';
import GaiaTooltip, { gaiaTooltipProps } from '../../base/GaiaTooltip.vue';
import { IconDocument } from '@/components/design/icons';
import GaiaAvatar from '../../base/GaiaAvatar.vue';
import GaiaStatusIndicator, { type GaiaIndicatorStatus } from '../../base/GaiaStatusIndicator.vue';
import GaiaText from '../../base/GaiaText.vue';
import GaiaVersionBadge from '../../base/GaiaVersionBadge.vue';
import { gaiaAccent, gaiaNeutral } from '../../themes/tokens';

const props = defineProps({
  /** The uploaded file's name. */
  name: {
    type: String,
    required: true,
  },
  /** Formatted upload timestamp, under the name. */
  uploadedAt: {
    type: String,
    default: undefined,
  },
  /** The current revision's version number — a chip after the date, from v2 on, as in the vault. */
  version: {
    type: Number,
    default: undefined,
  },
  /** A further line under the date — e.g. why the file did not convert. */
  sub: {
    type: String,
    default: undefined,
  },
  /** alert = the sub line is a failure reason and reads in the alert colour. */
  subTone: {
    type: String as PropType<'default' | 'alert'>,
    default: 'default',
  },
  /** Intake status — adapt VaultFileIntakeStatus via statusMaps.vaultFileIntakeStatusMap. */
  status: {
    type: String as PropType<GaiaIndicatorStatus>,
    default: 'neutral',
  },
  statusLabel: {
    type: String,
    required: true,
  },
  /** Who uploaded the file — named on the avatar's hover. */
  uploadedBy: {
    type: String,
    default: undefined,
  },
  /** That person's initials — derived by the caller, per `initialsOf`. */
  uploadedByInitials: {
    type: String,
    default: '',
  },
  /** The row the detail pane is showing. */
  selected: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{ select: [] }>();

const rowBorder = gaiaNeutral.borderSoft;
const hoverWash = gaiaNeutral.surfaceHover;
const selectedBg = gaiaNeutral.surfaceActive;
const iconColor = gaiaNeutral.textSubtle;
const focusRing = gaiaAccent.base;
</script>

<template>
  <!-- One intake file in the History list (mock: Contracts
       Overview, list mode): the status as a bare dot on the left, the name
       on one line over the uploader's avatar and the upload date (and a
       failure reason, if any), the file glyph at the right edge — the playbook run rail's rhythm, kept to two lines so
       the list stays dense.
       Not GaiaFileRow: that row has three fixed states, no status label, and a
       wrapping vault path; this one carries the intake's own status vocabulary.
       The whole row selects; the name is the keyboard's way in. -->
  <div class="gaia-intake-row" :class="{ selected: props.selected }" @click="emit('select')">
    <!-- Unlabelled: the dot alone carries the status, so its label shows in a
         tooltip on hover and is what a screen reader announces. -->
    <gaia-tooltip placement="right">
      <template #trigger>
        <span class="status" role="img" :aria-label="props.statusLabel">
          <gaia-status-indicator :status="props.status" glyph="dot" />
        </span>
      </template>
      {{ props.statusLabel }}
    </gaia-tooltip>
    <span class="content">
      <button
        type="button"
        class="name"
        :aria-current="props.selected ? 'true' : undefined"
        @click.stop="emit('select')"
      >
        <!-- Clipped by n-ellipsis, which hands a clipped name a hover tooltip
             with the full text (GaiaRunResultRow's idiom). Keep the slot plain
             text: it is cloned into the bubble. -->
        <gaia-text tag="div" size="dense" tone="ink" weight="medium">
          <n-ellipsis :tooltip="gaiaTooltipProps" class="name-text">{{ props.name }}</n-ellipsis>
        </gaia-text>
      </button>
      <!-- The disc rather than the address, as in the playbook run rail: the
           list is too narrow to spell one out, and its hover names the person. -->
      <n-flex
        v-if="props.uploadedAt || props.uploadedBy || (props.version && props.version > 1)"
        align="center"
        :size="6"
        :wrap="false"
      >
        <gaia-tooltip v-if="props.uploadedBy">
          <template #trigger>
            <gaia-avatar :size="18">{{ props.uploadedByInitials }}</gaia-avatar>
          </template>
          {{ props.uploadedBy }}
        </gaia-tooltip>
        <gaia-text
          v-if="props.uploadedAt"
          size="meta"
          tone="subtle"
          :style="{ fontVariantNumeric: 'tabular-nums' }"
        >
          {{ props.uploadedAt }}
        </gaia-text>
        <gaia-version-badge v-if="props.version && props.version > 1" :version="props.version" />
      </n-flex>
      <gaia-text
        v-if="props.sub"
        truncate
        tag="div"
        size="nano"
        :tone="props.subTone === 'alert' ? 'alert' : 'tertiary'"
        :title="props.sub"
      >
        {{ props.sub }}
      </gaia-text>
    </span>
    <n-icon :component="IconDocument" :size="14" class="glyph" />
  </div>
</template>

<style scoped>
.gaia-intake-row {
  display: grid;
  grid-template-columns: 14px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: start;
  padding: 8px 16px;
  border-bottom: 1px solid v-bind(rowBorder);
  cursor: pointer;
}

.gaia-intake-row:hover {
  background: v-bind(hoverWash);
}

.gaia-intake-row.selected {
  background: v-bind(selectedBg);
}

/* Sits on the name's first line rather than the row's centre. */
.glyph {
  margin-top: 2px;
  color: v-bind(iconColor);
}

.status {
  display: flex;
  align-items: center;
  height: 18px;
}

.content {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* Stripped to its semantics: the row draws the surface and the cursor. Full
   width, so the ellipsis has a box to clip against. */
.name {
  display: block;
  width: 100%;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

/* n-ellipsis is inline-block; the cap lets it clip at the column's edge. */
.name-text {
  max-width: 100%;
  vertical-align: top;
}

.name:focus-visible {
  outline: 2px solid v-bind(focusRing);
  outline-offset: 2px;
  border-radius: 4px;
}
</style>
